/**
 * @fileoverview System email template service — local-first CRUD for system-scoped email templates.
 * @module services/systemEmailTemplateService
 *
 * Responsibilities:
 * - CRUD operations for system email templates in Dexie.
 * - Publish/archive lifecycle management.
 * - Library toggle and tenant visibility controls.
 * - Audit logging for all mutations.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - packages/audit (runAuditedMutation)
 * - ./systemAuditLogService (writeAuditLog)
 *
 * Architecture:
 * - Local-first: Dexie is source of truth; background sync reconciles with MongoDB.
 * - Optimistic writes with wakeLocalWrite for push sync.
 *
 * @localFirst
 * Source of Truth: local (Dexie)
 * Write Flow: Dexie write → wakeLocalWrite → sync push to MongoDB
 * Sync Strategy: Optimistic write; background push
 * Conflict Resolution: Last-write-wins (server timestamp)
 *
 * Notes:
 * - publish/archive/toggleLibrary/setVisibility delegate to updateEmailTemplate
 *   and are therefore audited through the update path.
 * - updateEmailTemplate bumps `version` on every mutation (parity with the
 *   tenant service) and clears publishedAt/publishedBy when a patch moves
 *   status back to `draft`; publishEmailTemplate stamps publishedAt/publishedBy.
 * - createEmailTemplate always starts new templates as `draft` and requires
 *   the audit actor (requireActorId throws when identity is not ready —
 *   parity with the tenant service's getCurrentUserId).
 * - duplicateEmailTemplate strips publish stamps/library flag/actor fields so
 *   the copy is a fresh draft authored by the duplicating admin.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import type { EmailTemplateLocal } from '@firesquirrel/local-db';
import { runAuditedMutation } from '../../../packages/audit/src';
import { writeAuditLog, getSystemAuditUser } from './systemAuditLogService';

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Resolves the current system-admin actor for audit stamping.
 * Throws when the actor identity is not ready — a silent 'system' fallback
 * would stamp misleading audit fields on published/deleted records.
 */
function requireActorId(): string {
  const actor = getSystemAuditUser();
  if (!actor?.userId) {
    throw new Error('[auth] system audit actor is not ready');
  }
  return actor.userId;
}

export interface ListEmailTemplatesParams {
  status?: 'draft' | 'published' | 'archived';
  includeDeleted?: boolean;
}

export async function listEmailTemplates(
  params?: ListEmailTemplatesParams,
): Promise<EmailTemplateLocal[]> {
  const db = await getSystemDb();
  const results = await db.email_templates.toArray();
  let items = results as EmailTemplateLocal[];

  // Tombstones are excluded by default (parity with the tenant service);
  // pass includeDeleted to audit the full local cache.
  if (!params?.includeDeleted) {
    items = items.filter((item) => !item._deleted);
  }

  if (params?.status) {
    items = items.filter((item) => item.status === params.status);
  }

  return items.sort((a, b) => {
    const aDate = new Date(a.updatedAt || a.createdAt).getTime();
    const bDate = new Date(b.updatedAt || b.createdAt).getTime();
    return bDate - aDate;
  });
}

export async function getEmailTemplate(id: string): Promise<EmailTemplateLocal | null> {
  const db = await getSystemDb();
  const doc = await db.email_templates.get(id);
  return (doc as EmailTemplateLocal | undefined) ?? null;
}

/** @process createEmailTemplate — Dexie mutation + audit entry. */
export async function createEmailTemplate(
  data: Partial<EmailTemplateLocal>,
): Promise<EmailTemplateLocal> {
  const db = await getSystemDb();
  const now = nowIso();
  const newId = data._id || `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return runAuditedMutation<null, EmailTemplateLocal>({
    collection: 'emailTemplates',
    documentId: newId,
    action: 'create',
    summary: `Created system email template: ${data.name ?? 'Untitled'}`,
    getBefore: async () => null,
    execute: async () => {
      const newDoc = {
        ...data,
        _id: newId,
        status: 'draft' as const,
        createdBy: data.createdBy ?? requireActorId(),
        updatedBy: data.updatedBy ?? data.createdBy ?? requireActorId(),
        scope: data.scope ?? { level: 'system' },
        createdAt: now,
        updatedAt: now,
        version: 1,
        _deleted: false,
      };
      await db.email_templates.put(newDoc as any);
      wakeLocalWrite('email_templates');
      return newDoc as EmailTemplateLocal;
    },
    getAfter: async () => (await db.email_templates.get(newId)) as EmailTemplateLocal | null,
    writeAuditLog,
  });
}

/** @process updateEmailTemplate — Dexie mutation + audit entry. */
export async function updateEmailTemplate(
  id: string,
  patch: Partial<EmailTemplateLocal>,
): Promise<EmailTemplateLocal | null> {
  const db = await getSystemDb();
  const existing = await db.email_templates.get(id);
  if (!existing) return null;

  return runAuditedMutation<EmailTemplateLocal, EmailTemplateLocal>({
    collection: 'emailTemplates',
    documentId: id,
    action: 'update',
    summary: `Updated system email template: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.email_templates.get(id)) as EmailTemplateLocal | null,
    execute: async () => {
      // Moving back to draft clears the publish stamps — the publish flow is
      // the only path that (re)stamps publishedAt/publishedBy.
      const clearStamps = patch.status === 'draft'
        ? { publishedAt: undefined, publishedBy: undefined }
        : {};
      const updated = {
        ...(existing as any),
        ...patch,
        ...clearStamps,
        version: ((existing as any).version ?? 1) + 1,
        updatedAt: new Date().toISOString(),
      };
      await db.email_templates.put(updated);
      wakeLocalWrite('email_templates');
      return updated as EmailTemplateLocal;
    },
    getAfter: async () => (await db.email_templates.get(id)) as EmailTemplateLocal | null,
    writeAuditLog,
  });
}

/** @process deleteEmailTemplate — Dexie mutation + audit entry. */
export async function deleteEmailTemplate(id: string): Promise<boolean> {
  const db = await getSystemDb();
  const existing = await db.email_templates.get(id);
  if (!existing) return false;

  await runAuditedMutation<EmailTemplateLocal, EmailTemplateLocal>({
    collection: 'emailTemplates',
    documentId: id,
    action: 'delete',
    summary: `Deleted system email template: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.email_templates.get(id)) as EmailTemplateLocal | null,
    execute: async () => {
      const tombstone = { ...(existing as any), _deleted: true, deletedAt: new Date().toISOString(), deletedBy: requireActorId(), updatedAt: new Date().toISOString() };
      await db.email_templates.put(tombstone);
      wakeLocalWrite('email_templates');
      return tombstone as EmailTemplateLocal;
    },
    getAfter: async () => (await db.email_templates.get(id)) as EmailTemplateLocal | null,
    writeAuditLog,
  });

  return true;
}

/** @process duplicateEmailTemplate — delegates to audited mutation. */
export async function duplicateEmailTemplate(id: string): Promise<EmailTemplateLocal | null> {
  const original = await getEmailTemplate(id);
  if (!original) return null;

  const copy = {
    ...original,
    _id: undefined,
    name: `${original.name} (Copy)`,
    status: 'draft' as const,
    // A copy is a fresh draft authored by the duplicating admin: no publish
    // stamps, not in the library, and createEmailTemplate stamps the actor.
    publishedAt: undefined,
    publishedBy: undefined,
    isInLibrary: undefined,
    createdBy: undefined,
    updatedBy: undefined,
  };

  return createEmailTemplate(copy);
}

/** @process publishEmailTemplate — stamps publishedAt/publishedBy, delegates to audited mutation. */
export async function publishEmailTemplate(id: string): Promise<EmailTemplateLocal | null> {
  return updateEmailTemplate(id, {
    status: 'published',
    publishedAt: nowIso(),
    publishedBy: requireActorId(),
  });
}

/** @process archiveEmailTemplate — delegates to audited mutation. */
export async function archiveEmailTemplate(id: string): Promise<EmailTemplateLocal | null> {
  return updateEmailTemplate(id, {
    status: 'archived',
  });
}

// ---------------------------------------------------------------------------
// Library toggle — sets isInLibrary flag on an email template
// Note: This only toggles the library flag. Template must be published separately.
// ---------------------------------------------------------------------------
/** @process toggleEmailTemplateLibrary — delegates to audited mutation. */
export async function toggleEmailTemplateLibrary(
  id: string,
  isInLibrary: boolean,
): Promise<EmailTemplateLocal | null> {
  return updateEmailTemplate(id, { isInLibrary });
}

// ---------------------------------------------------------------------------
// Visibility — controls which tenants can see this email template in library
// ---------------------------------------------------------------------------
/** @process setEmailTemplateVisibility — delegates to audited mutation. */
export async function setEmailTemplateVisibility(
  id: string,
  visibility: 'all-departments' | 'specific-tenants' | 'system-only',
  allowedTenantIds?: string[],
): Promise<EmailTemplateLocal | null> {
  const patch: Partial<EmailTemplateLocal> = { visibility };
  if (visibility === 'specific-tenants' && allowedTenantIds) {
    patch.allowedTenantIds = allowedTenantIds;
  } else {
    patch.allowedTenantIds = undefined;
  }
  return updateEmailTemplate(id, patch);
}
